import React, { useState } from "react";
import { useFormik } from "formik";
import { Box, TextField, HStack, Button } from "../../../components";
import "./LineItem.scss";
// eslint-disable-next-line import/no-named-as-default,import/no-named-as-default-member
import { LineItemSchema } from "../../../utils/Validation";
import { IProduct } from "../../../types/CnpInfo";
import ErrorPrompt from "../../../components/ErrorPrompt/ErrorPrompt";
import centsToRand, { numberWithSpaces } from "../../../utils/utils";

interface LineItemProps {
  onCancel: any;
  onSubmit: any;
  lineItem: IProduct;
  lineItemTally: number;
  paymentLimit: number;
}

const LineItemContent = ({ lineItem, onSubmit, onCancel, lineItemTally, paymentLimit }: LineItemProps) => {
  const paymentLimitText = `Invoice total cannot exceed R ${numberWithSpaces(
    centsToRand(paymentLimit),
  )}. Please adjust your items to a lesser price or quantity.`;

  const [exceedsLimit, setExceedsLimit] = useState(false);
  const lineItemForm = useFormik({
    initialValues: {
      itemName: lineItem?.itemName || "",
      description: lineItem?.description || "",
      price: lineItem?.price || "",
      lineItemQuantity: lineItem?.lineItemQuantity || "",
      vatIncluded: lineItem?.vatIncluded === undefined ? false : lineItem?.vatIncluded,
    },
    validationSchema: LineItemSchema,
    onSubmit: () => {},
  });

  const handleSubmit = () => {
    onSubmit(lineItemForm.values);
  };

  const handleCancel = () => {
    onCancel();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      e.preventDefault();
    }
  };

  const handleItemCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExceedsLimit(false);
    lineItemForm.handleChange(e);
    // form events are async: need current values
    let price = 0;
    let quantity = 0;
    switch (e.target.name.toLowerCase()) {
      case "price":
        price = Number(e.target.value);
        quantity = Number(lineItemForm.values.lineItemQuantity);
        break;
      case "lineitemquantity":
        price = Number(lineItemForm.values.price);
        quantity = Number(e.target.value);
        break;
      default:
    }
    const totalIncludingCurrentItem = lineItemTally + price * quantity;
    if (totalIncludingCurrentItem > paymentLimit / 100) setExceedsLimit(true);
  };
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="px-4 content" onKeyDown={onKeyDown}>
      {exceedsLimit && (
        <div className="error-container">
          <ErrorPrompt text={paymentLimitText} />
        </div>
      )}
      <div className="line-item-form">
        <Box className="px-0 pb-4">
          <div className="pb-3">
            <TextField
              name="itemName"
              className={lineItemForm.touched.itemName && lineItemForm.errors.itemName && "border-red"}
              value={lineItemForm.values.itemName}
              onChange={lineItemForm.handleChange}
              onBlur={lineItemForm.handleBlur("itemName")}
              errorMessage={lineItemForm.touched.itemName && lineItemForm.errors.itemName}
              status={!lineItemForm.errors.itemName && lineItemForm.values.itemName && "complete"}
              label="Item Name"
              type="text"
              placeholder=""
              maxLength={100}
              required
            />
          </div>
          <div className="pb-3">
            <TextField
              name="description"
              className={lineItemForm.touched.description && lineItemForm.errors.description && "border-red"}
              value={lineItemForm.values.description}
              onBlur={lineItemForm.handleBlur("description")}
              onChange={lineItemForm.handleChange}
              errorMessage={lineItemForm.touched.description && lineItemForm.errors.description}
              status={!lineItemForm.errors.description && lineItemForm.values.description && "complete"}
              label="Description"
              placeholder=""
              type="text"
              maxLength={100}
              required
            />
          </div>
          <div className="pb-3">
            <TextField
              name="price"
              className={(exceedsLimit || (lineItemForm.touched.price && lineItemForm.errors.price)) && "border-red"}
              value={lineItemForm.values.price}
              onChange={handleItemCostChange}
              onBlur={lineItemForm.handleBlur("price")}
              errorMessage={lineItemForm.touched.price && lineItemForm.errors.price}
              status={!lineItemForm.errors.price && lineItemForm.values.price && "complete"}
              label="Price"
              type="number"
              placeholder=""
              prefix="R"
              required
            />
          </div>
          <div className="last-row pb-4">
            <div>
              <TextField
                name="lineItemQuantity"
                className={
                  (exceedsLimit || (lineItemForm.touched.lineItemQuantity && lineItemForm.errors.lineItemQuantity)) &&
                  "border-red"
                }
                value={lineItemForm.values.lineItemQuantity}
                onChange={handleItemCostChange}
                onBlur={lineItemForm.handleBlur("lineItemQuantity")}
                errorMessage={lineItemForm.touched.lineItemQuantity && lineItemForm.errors.lineItemQuantity}
                status={!lineItemForm.errors.lineItemQuantity && lineItemForm.values.lineItemQuantity && "complete"}
                label="Quantity"
                type="text"
                placeholder=""
                maxLength={5}
                required
              />
            </div>
            <div className="checkbox-container">
              <input
                name="vatIncluded"
                className="vat-checkbox"
                type="checkbox"
                checked={lineItemForm.values.vatIncluded}
                onChange={lineItemForm.handleChange}
              />
              <div className="text-bold text-charcoal text-size-20">VAT (15%)</div>
            </div>
          </div>
          <div id="mobile-buttons">
            <div className="pb-2">
              <Button
                onClick={handleSubmit}
                className="text-size-18"
                label="Save"
                size="medium"
                type="button"
                disabled={!lineItemForm.dirty || !lineItemForm.isValid || exceedsLimit}
              />
            </div>
            <HStack className="pb-2 justify-content-center">
              <button
                type="button"
                className="cancel-button pl-5 my-0 ml-1 mr-5 text-size-18 text-medium clickable"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </HStack>
          </div>
          <div id="desktop-buttons">
            <Button type="button" label="Cancel" varient="secondary" size="small" onClick={handleCancel} />
            <Button
              onClick={handleSubmit}
              label="Save"
              size="small"
              type="button"
              disabled={!lineItemForm.dirty || !lineItemForm.isValid || exceedsLimit}
            />
          </div>
        </Box>
      </div>
    </div>
  );
};

export default LineItemContent;
